import { useState } from "react";
import type { RoomsBoardPreferences } from "../components/TodayRoomsBoard";

export type TodayBoardView = "next" | "rooms";

// Only a view preference is retained; no patient data or credentials enter browser storage.
// The workforce session object separates authenticated runtimes, and each site has its own view.
const viewsBySession = new WeakMap<object, Map<string, TodayBoardView>>();
const staffBySession = new WeakMap<object, Map<string, boolean>>();
const roomsBySession = new WeakMap<object, Map<string, RoomsBoardPreferences>>();

export function useTodayBoardView(session: object, siteId: string) {
  const read = () => viewsBySession.get(session)?.get(siteId) ?? "next";
  const [selection, setSelection] = useState(() => ({ session, siteId, view: read() }));
  const view =
    selection.session === session && selection.siteId === siteId ? selection.view : read();
  const setView = (next: TodayBoardView) => {
    let views = viewsBySession.get(session);
    if (!views) {
      views = new Map();
      viewsBySession.set(session, views);
    }
    views.set(siteId, next);
    setSelection({ session, siteId, view: next });
  };
  return [view, setView] as const;
}

export function clearTodayBoardView(session: object): void {
  viewsBySession.delete(session);
  roomsBySession.delete(session);
  staffBySession.delete(session);
}

/** Keep explicit room filters and density when returning from an encounter. */
export function useTodayRoomsPreferences(session: object, siteId: string) {
  const read = () => roomsBySession.get(session)?.get(siteId);
  const [selection, setSelection] = useState(() => ({ session, siteId, preferences: read() }));
  const preferences =
    selection.session === session && selection.siteId === siteId ? selection.preferences : read();
  const setPreferences = (next: RoomsBoardPreferences) => {
    let sites = roomsBySession.get(session);
    if (!sites) {
      sites = new Map();
      roomsBySession.set(session, sites);
    }
    sites.set(siteId, next);
    setSelection({ session, siteId, preferences: next });
  };
  return [preferences, setPreferences] as const;
}

/** Staff queue visibility is independent of the main board view. */
export function useTodayStaffPanelOpen(session: object, siteId: string) {
  const read = () => staffBySession.get(session)?.get(siteId) ?? false;
  const [selection, setSelection] = useState(() => ({ session, siteId, open: read() }));
  const open =
    selection.session === session && selection.siteId === siteId ? selection.open : read();
  const setOpen = (next: boolean) => {
    let sites = staffBySession.get(session);
    if (!sites) {
      sites = new Map();
      staffBySession.set(session, sites);
    }
    sites.set(siteId, next);
    setSelection({ session, siteId, open: next });
  };
  return [open, setOpen] as const;
}
